import styled from '@emotion/styled';
import { Button, TableColumnsType, Tag, Tooltip } from 'antd';
import ScrollTable, { ScrollTableProps } from 'components/ScrollTable';
import { formatNumber } from 'utils/numberFormat';
import { ReactNode } from 'react';
import { What } from 'views/Marketing/components/PolicyTable';
import { useBuyerTypeLabelMap } from './AssistantDetailtable';

type RT = Dashboard.ReceptionScore.Stat;

export interface StatTableProps extends ScrollTableProps<RT> {
  onCheckDetail: (record: RT) => void;
}

function StatTable(props: StatTableProps) {
  const { onCheckDetail, ...otherProps } = props;

  const { t, buyerTypeLabelMap } = useBuyerTypeLabelMap();

  const dataRender = (total: ReactNode, preSale: ReactNode, afterSale: ReactNode) => {
    return (
      <Data>
        {total}
        <div>
          <Tag color="blue">
            {buyerTypeLabelMap.PRE_SALE}
            {preSale}
          </Tag>
        </div>
        <div>
          <Tag color="orange">
            {buyerTypeLabelMap.AFTER_SALE}
            {afterSale}
          </Tag>
        </div>
      </Data>
    );
  };

  const columns: TableColumnsType<RT> = [
    {
      key: 'assistantId',
      dataIndex: 'assistantId',
      title: 'ID',
      fixed: 'left',
    },
    {
      key: 'assistantName',
      dataIndex: 'assistantName',
      title: t('aide.帐号昵称'),
      fixed: 'left',
    },
    {
      key: 'inviteCount',
      dataIndex: 'inviteCount',
      sorter: true,
      showSorterTooltip: false,
      title: (
        <div>
          {t('aide.评分邀请量')}
          <Tooltip title={t('aide.系统发起评分邀请的总数量')}>
            <What />
          </Tooltip>
        </div>
      ),
      render: (_, record) => {
        const { inviteCount, preSaleInviteCount, afterSaleInviteCount } = record;

        const formatter = (num: number) => formatNumber(num, '0,0');

        return dataRender(
          formatter(inviteCount),
          formatter(preSaleInviteCount),
          formatter(afterSaleInviteCount),
        );
      },
    },
    {
      key: 'replyRate',
      title: (
        <div>
          {t('aide.评分率')}
          <Tooltip
            title={t('aide.系统发起评分邀请后，买家给出评分的比例 (买家评分量 / 评分邀请量)')}
          >
            <What />
          </Tooltip>
        </div>
      ),
      sorter: (a, b) => parseFloat(a.replyRate) - parseFloat(b.replyRate),
      showSorterTooltip: false,
      render: (_, record) => {
        const { replyRate, preSaleReplyRate, afterSaleReplyRate } = record;

        return dataRender(replyRate, preSaleReplyRate, afterSaleReplyRate);
      },
    },
    {
      key: 'praiseRate',
      title: (
        <div>
          {t('aide.好评率')}
          <Tooltip title={t('aide.系统发起评分邀请后，买家给出好评的比例 (好评量/买家评分量)')}>
            <What />
          </Tooltip>
        </div>
      ),
      sorter: (a, b) => parseFloat(a.praiseRate) - parseFloat(b.praiseRate),
      showSorterTooltip: false,
      render: (_, record) => {
        const { praiseRate, preSalePraiseRate, afterSalePraiseRate } = record;

        return dataRender(praiseRate, preSalePraiseRate, afterSalePraiseRate);
      },
    },
    {
      key: 'operation',
      title: t('操作'),
      fixed: 'right',
      render: (_, record) => {
        return (
          <Button type="link" onClick={() => onCheckDetail(record)}>
            {t('aide.查看评分明细')}
          </Button>
        );
      },
    },
  ];

  return <ScrollTable rowKey="assistantId" columns={columns} {...otherProps} />;
}

const Data = styled.div`
  > div {
    margin-top: 4px;
  }
`;

export default StatTable;
